import {
  Box,
  Chip,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import PropTypes from 'prop-types'
import ButtonFormModal from './ButtonFormModal'
import { ACTION_TYPES, AUDIENCES, BLUE, blueSwitchSx, isRenderable, labelFor } from '../utils'

const headCellSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  color: BLUE.mutedText,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
}

const chipSx = {
  height: 22,
  fontSize: 11,
  backgroundColor: BLUE.soft,
  color: BLUE.dark,
  border: `1px solid ${BLUE.line}`,
}

/** What the chip does, and the value behind it — shown truncated. */
const ActionDetail = ({ button }) => {
  const isLink = button.actionType === 'link'
  const text = isLink ? button.link : button.prompt

  return (
    <Box>
      <Typography variant="caption" sx={{ display: 'block', color: BLUE.main, fontWeight: 600 }}>
        {labelFor(ACTION_TYPES, button.actionType)}
        {isLink && button.newTab ? ' · new tab' : ''}
      </Typography>

      {text ? (
        <Tooltip title={text}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              maxWidth: 260,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {text}
          </Typography>
        </Tooltip>
      ) : (
        <Typography variant="caption" color="error" sx={{ display: 'flex', gap: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 14 }} />
          Nothing set
        </Typography>
      )}
    </Box>
  )
}

ActionDetail.propTypes = { button: PropTypes.object.isRequired }

export default function ButtonsTable({ buttons, onChange, onMove, onRemove }) {
  if (!buttons.length) {
    return (
      <Box
        sx={{
          py: 5,
          textAlign: 'center',
          borderRadius: 2,
          border: `1px dashed ${BLUE.line}`,
          backgroundColor: BLUE.mutedBg,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No buttons yet. Add one and it appears under the chat greeting.
        </Typography>
      </Box>
    )
  }

  return (
    <Table size="small">
      <TableHead sx={{ display: 'table-header-group' }}>
        <TableRow>
          <TableCell sx={{ ...headCellSx, width: 70 }}>Order</TableCell>
          <TableCell sx={headCellSx}>Label</TableCell>
          <TableCell sx={headCellSx}>Audience</TableCell>
          <TableCell sx={headCellSx}>Action</TableCell>
          <TableCell sx={{ ...headCellSx, width: 80 }}>Live</TableCell>
          <TableCell sx={{ ...headCellSx, width: 90 }} align="right" />
        </TableRow>
      </TableHead>

      <TableBody>
        {buttons.map((button, index) => {
          const live = isRenderable(button)

          return (
            <TableRow key={button._id || `new-${index}`} hover>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="Move up">
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => onMove(index, -1)}
                    >
                      <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <IconButton
                      size="small"
                      disabled={index === buttons.length - 1}
                      onClick={() => onMove(index, 1)}
                    >
                      <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>

              <TableCell>
                <Typography
                  variant="button"
                  fontWeight={600}
                  sx={{ color: live ? 'text.primary' : BLUE.mutedText }}
                >
                  {button.label || '(no label)'}
                </Typography>
              </TableCell>

              <TableCell>
                <Chip size="small" label={labelFor(AUDIENCES, button.audience)} sx={chipSx} />
              </TableCell>

              <TableCell>
                <ActionDetail button={button} />
              </TableCell>

              <TableCell>
                <Switch
                  size="small"
                  checked={Boolean(button.isActive)}
                  onChange={(e) => onChange(index, { ...button, isActive: e.target.checked })}
                  sx={blueSwitchSx}
                />
              </TableCell>

              <TableCell align="right">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <ButtonFormModal
                    button={button}
                    onSubmit={(updated) => onChange(index, { ...button, ...updated })}
                  />
                  <Tooltip title="Remove button">
                    <IconButton size="small" color="error" onClick={() => onRemove(index)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

ButtonsTable.propTypes = {
  buttons: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
}
